import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Grid as Client } from './client'
import { Grid as Server } from './server'
import { ASC, DESC, MENU_ID } from './constants'
import { Pagination } from './pagination'
import { NoSearchResults } from './noSearchResults'

const defaultOptions = {
  csvExportName: 'export.csv',
  isSearchQueryHighlighted: false,
  disableFilters: false
}
const defaultOnClearSearch = () => {}
const defaultOnSelectRow = () => {}
const defaultOnChangePageSize = () => {}
const defaultIsRowDisabled = () => false
/** The Grid component displays tabular data for sorting, paging and searching. */
const Grid = props => {
  const overrides = useMemo(() => {
    const defaulted = {
      options: { ...defaultOptions, ...props.options },
      isRowDisabled: props.isRowDisabled || defaultIsRowDisabled,
      onClearSearch: props.onClearSearch || defaultOnClearSearch,
      onChangePageSize: props.onChangePageSize || defaultOnChangePageSize,
      onSelectRow: props.onSelectRow || defaultOnSelectRow,
      NoSearchResultsComponent: props.noSearchResultsComponent || NoSearchResults
    }

    if (props.dateRangePicker === true) {
      defaulted.dateRangePicker = { minStartDate: null, maxEndDate: null }
    }

    return defaulted
  }, [
    props.options,
    props.isRowDisabled,
    props.onClearSearch,
    props.onSelectRow,
    props.dateRangePicker,
    props.onChangePageSize,
    props.noSearchResultsComponent
  ])

  if (props.server) {
    return <Server {...props} {...overrides} />
  }

  return <Client {...props} {...overrides} />
}

Grid.propTypes = {
  /** The data rows in the table. */
  data: PropTypes.array,
  /** The table column headers. */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      /** A unique identifier for the column header, usually a key from an object in the data. */
      id: PropTypes.string.isRequired,
      /** The column header label. */
      label: PropTypes.string.isRequired,
      /** Whether the table can be sorted on this column. If `sort` is not defined, sort function will be defaulted. */
      sortable: PropTypes.bool,
      /** Render function for the row data in this column. Is passed the data object for this row. */
      render: PropTypes.func,
      /** Custom sort function (rowA, rowB, 'asc' | 'desc') for the column's data. */
      sort: PropTypes.func,
      /** Set the width of the column, e.g. '200px, or '30%'. */
      width: PropTypes.string,
      /** Define what keys of row objects to inspect for searching this column. */
      searchKeys: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.oneOf(['string', 'number', 'date'])
        })
      )
    })
  ).isRequired,
  /** Identify what field on a row serves as an ID (react key). */
  rowIdKey: PropTypes.string,
  /** Determine whether a row should be styled as disabled. Passed the row data, rowData => boolean. */
  isRowDisabled: PropTypes.func,
  /** Handler for click events on table rows. Passed the event object and row data. */
  onClickRow: PropTypes.func,
  /** Handler for change events on checkboxes within rows. Passed the id value of current selection and array of all selections. */
  onSelectRow: PropTypes.func,
  /** Receives the current search query. Called when the user clears the search query. */
  onClearSearch: PropTypes.func,
  /** Handler for changing page size. Recieves the seleted page size. */
  onChangePageSize: PropTypes.func,
  /** The column.id the table is sorted by on page load. */
  defaultSortBy: PropTypes.string,
  /** The direction of the sort order on page load. */
  defaultSortOrder: PropTypes.oneOf([ASC, DESC]),
  /** The page the starting table page on page load. */
  defaultPage: PropTypes.number,
  /** The number of rows per page on page load. */
  defaultPageSize: PropTypes.oneOf(Pagination.lengths),
  /** Whether the pager and page length controls are visible. */
  isPagerVisible: PropTypes.bool,
  /** Whether you can use a checkbox to select individual rows and/or pages. */
  isRowSelectable: PropTypes.bool,
  /** What type of client-side search to perform. */
  searchType: PropTypes.oneOf(['exact', 'matches', 'fuzzy']),
  /** Whether the data should be reloaded from the server. */
  shouldReload: PropTypes.bool,
  /** Whether the selected rows should be cleared. */
  shouldClearSelected: PropTypes.bool,
  /** Whether the search input has a date range picker. */
  dateRangePicker: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      /** Defines the lower bound on the start date for the date range picker. */
      minStartDate: PropTypes.instanceOf(Date),
      /** Defines the upper bound on the end date for the date range picker. */
      maxEndDate: PropTypes.instanceOf(Date)
    })
  ]),
  /** Component to display when search returns no data. Receives the current search query and date range when using `dateRangePicker`. */
  noSearchResultsComponent: PropTypes.elementType,
  /** Fetch table data from a server. */
  server: PropTypes.shape({
    /** Determines UX for how data is loaded into the table. `search` requires a search query, `filter` does not. */
    mode: PropTypes.oneOf(['filter', 'search']).isRequired,
    /** What text to display under the search box to help prompt the user. */
    searchModeText: PropTypes.string,
    /** Custom fetch function with the same signature as window.fetch. Must parse and return response body. */
    fetch: PropTypes.func,
    /** Set the HTTP method used for server requests. */
    method: PropTypes.oneOf(['GET', 'POST']),
    /** The server endpoint to page, sort and search. */
    url: PropTypes.string.isRequired,
    /** Receives the response data from the server. Should return the table data from the response. */
    format: PropTypes.func,
    /** The request headers sent to the server. */
    headers: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    /** Receives the current sort direction and column.id. Should return query string for sorting at `server.url`. */
    sort: PropTypes.func.isRequired,
    /** Receives the current page size and page. Should return query string for paging at `server.url`. */
    pagination: PropTypes.func.isRequired,
    /** Receives the current search query and date range when using `dateRangePicker`. Should return query string for searching at `server.url`. */
    search: PropTypes.func.isRequired,
    /** Receives the server response. Should return the total records in the data set. */
    total: PropTypes.func.isRequired,
    /** Callback to execute when the server successfully fetches data. Receives the response data and search query. */
    onSuccess: PropTypes.func,
    /** Callback to execute when there is a server error. Receives the error object. */
    onError: PropTypes.func,
    /** Export configuration. Contains functions that call export endpoints. */
    export: PropTypes.shape({
      /** Receives the table state (pagination, sorting, searching, etc.). Returns a promise that resolves to a CSV export response. */
      csv: PropTypes.func
    })
  }),
  /** Optional props that all have defaults. */
  options: PropTypes.shape({
    /** Disable the filter bar internal to the grid, for cases when no filtering is necessary or when filtering is handled externally **/
    disableFilters: PropTypes.bool,
    /** Whether the query text should be highlighted in the search results. */
    isSearchQueryHighlighted: PropTypes.bool,
    /** The name of the downloaded file for CSV exports. */
    csvExportName: PropTypes.string
  }),
  /** Bulk actions that can be performed on multiple rows. Requires rowIdKey to be set. */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      /** The name of the action button. */
      label: PropTypes.string.isRequired,
      /** The icon of the action button. */
      icon: PropTypes.elementType,
      /** Callback to excecute when the action is triggered. Receives the rowIdKey of the selected rows. */
      onAct: PropTypes.func.isRequired
    })
  )
}
Grid.ASC = ASC
Grid.DESC = DESC
Grid.MENU_ID = MENU_ID

export { Grid }
